import React, { useMemo, useEffect } from 'react';
import { Loader } from '@aws-amplify/ui-react';
import { ThemeProvider } from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify';
import useHealthCheck from 'hooks/useHealthCheck';
import { useLabelOptions } from './contexts/labelOptionsContext';
import { useLocaleState } from './contexts/localeContext';
import { useToast } from 'contexts/toastContext';
import { AuthorizedUserContextProvider, useAuthorizedUserContext } from './contexts/authorizedUserContext';
import { getTheme } from './theme';
import awsAmplifyInitializer from 'initializers/awsAmplify';

import MaintenancePage from './components/MaintenancePage';
import AuthPage from './components/AuthPage';
import AccessRestrictedByIpPage from './components/AccessRestrictedByIpPage';
import SsoFailurePage from 'components/SsoFailurePage';
import Toasts from 'components/Toast';
import PhoneChangePage from 'components/PhoneChangePage';
import Fullstory from 'components/Fullstory';

import '@aws-amplify/ui-react/styles.css';
import 'App.css';

function AmplifyListeners() {
  const { setAuthorizedUserContext } = useAuthorizedUserContext();

  useEffect(() => {
    awsAmplifyInitializer(setAuthorizedUserContext);
  }, [setAuthorizedUserContext]); // Empty dependency array - run only once

  return null;
}

function AppContent() {
  const { isMaintenance, healthCheckFirstInvoke, maintenanceTimeEnd } = useHealthCheck();
  const {
    authorizedUserContext: { phoneVerificationRequired, redirectError, isIpRestricted, ssoFailureMessage },
  } = useAuthorizedUserContext();
  const { setToastData } = useToast();

  // Check IP restriction from URL param or sessionStorage (checked on every render)
  const isAccessRestrictedByIp =
    new URLSearchParams(window.location.search).get('reason') === 'invalid_remote_address' ||
    sessionStorage.getItem('ipRestricted') === 'true';

  useEffect(() => {
    if (redirectError) {
      setToastData({
        type: 'error',
        message: I18n.get('auth.redirectErrorToastMessage'),
      });
    }
  }, [redirectError, setToastData]);

  // Show IP restricted page if either URL param is present OR context flag is set
  const showIpRestrictedPage = isAccessRestrictedByIp || isIpRestricted;
  const showSsoFailurePage = ssoFailureMessage !== undefined;

  return (
    <>
      <AmplifyListeners />
      {showIpRestrictedPage && <AccessRestrictedByIpPage />}
      {showSsoFailurePage && <SsoFailurePage message={ssoFailureMessage} />}
      {phoneVerificationRequired && <PhoneChangePage />}
      {healthCheckFirstInvoke && <Loader variation="linear" />}
      {isMaintenance && <MaintenancePage maintenanceTimeEnd={maintenanceTimeEnd} />}
      {!showIpRestrictedPage && !showSsoFailurePage && !isMaintenance && !healthCheckFirstInvoke && !phoneVerificationRequired && <AuthPage />}
      <Toasts />
    </>
  );
}

export default function App() {
  const { rtl } = useLocaleState();
  const { labelOptions } = useLabelOptions();
  const theme = useMemo(() => getTheme(labelOptions.brand), [labelOptions]);

  return (
    <ThemeProvider theme={theme} direction={rtl ? 'rtl' : 'ltr'}>
      <Fullstory />
      <AuthorizedUserContextProvider>
        <AppContent />
      </AuthorizedUserContextProvider>
    </ThemeProvider>
  );
}
